<script setup>
import { computed, ref } from 'vue';
import Input from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Avatar from 'next/avatar/Avatar.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  agentList: {
    type: Array,
    default: () => [],
  },
  selectedAgents: {
    type: Array,
    default: () => [],
  },
  updateSelectedAgents: {
    type: Function,
    default: () => {},
  },
  isWorking: {
    type: Boolean,
    default: false,
  },
  submitButtonText: {
    type: String,
    default: '',
  },
});

const { t } = useI18n();

const selectedAgentCount = computed(() => props.selectedAgents.length);

const search = ref('');
const filteredAgents = computed(() => {
  const query = search.value.trim().toLocaleLowerCase();
  return props.agentList.filter(agent =>
    `${agent.name || ''} ${agent.email || ''}`
      .toLocaleLowerCase()
      .includes(query)
  );
});
const allAgentsSelected = computed(
  () =>
    filteredAgents.value.length > 0 &&
    filteredAgents.value.every(agent => props.selectedAgents.includes(agent.id))
);
const someAgentsSelected = computed(
  () =>
    !allAgentsSelected.value &&
    filteredAgents.value.some(agent => props.selectedAgents.includes(agent.id))
);

const disableSubmitButton = computed(
  () => props.isWorking || selectedAgentCount.value === 0
);

const isAgentSelected = agentId => {
  return props.selectedAgents.includes(agentId);
};

const handleSelectAgent = agentId => {
  if (props.isWorking) return;
  const shouldRemove = isAgentSelected(agentId);

  let result = [];
  if (shouldRemove) {
    result = props.selectedAgents.filter(item => item !== agentId);
  } else {
    result = [...props.selectedAgents, agentId];
  }

  props.updateSelectedAgents(result);
};

const toggleSelectAll = () => {
  if (props.isWorking) return;
  const visibleIds = new Set(filteredAgents.value.map(agent => agent.id));
  const next = allAgentsSelected.value
    ? props.selectedAgents.filter(id => !visibleIds.has(id))
    : [...new Set([...props.selectedAgents, ...visibleIds])];
  props.updateSelectedAgents(next);
};
</script>

<template>
  <div class="space-y-3">
    <div class="flex flex-wrap items-center gap-3">
      <Input
        v-model="search"
        class="w-full sm:max-w-sm"
        :placeholder="t('TEAMS_SETTINGS.REDESIGN.SEARCH_AGENTS')"
        :aria-label="t('TEAMS_SETTINGS.REDESIGN.SEARCH_AGENTS')"
      />
      <label class="flex items-center gap-2 text-xs text-n-slate-11">
        <Checkbox
          :model-value="allAgentsSelected"
          :indeterminate="someAgentsSelected"
          :disabled="isWorking || !filteredAgents.length"
          @change="toggleSelectAll"
        />
        {{ t('TEAMS_SETTINGS.REDESIGN.VISIBLE') }}
      </label>
    </div>
    <div
      class="grid max-h-[24rem] gap-2 overflow-y-auto sm:grid-cols-2 lg:grid-cols-3"
    >
      <label
        v-for="agent in filteredAgents"
        :key="agent.id"
        class="flex min-w-0 cursor-pointer items-center gap-3 rounded-lg border border-n-weak p-3 transition-colors hover:bg-n-alpha-1"
        :class="isAgentSelected(agent.id) ? 'bg-n-alpha-2' : ''"
      >
        <Checkbox
          :model-value="isAgentSelected(agent.id)"
          :disabled="isWorking"
          :aria-label="agent.name"
          @change="() => handleSelectAgent(agent.id)"
        />
        <Avatar
          :src="agent.thumbnail"
          :name="agent.name"
          :status="agent.availability_status"
          :size="32"
          hide-offline-status
          rounded-full
          class="shrink-0"
        />
        <div class="min-w-0">
          <strong class="block truncate text-sm font-medium text-n-slate-12">{{
            agent.name
          }}</strong
          ><span class="block truncate text-xs text-n-slate-11">{{
            agent.email || '—'
          }}</span>
        </div>
      </label>
      <p
        v-if="!filteredAgents.length"
        class="py-6 text-center text-sm text-n-slate-11 sm:col-span-2 lg:col-span-3"
      >
        {{ t('TEAMS_SETTINGS.NO_RESULTS') }}
      </p>
    </div>
  </div>
  <div
    class="sticky bottom-0 z-20 mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-n-weak bg-n-solid-2 py-3"
  >
    <p class="mb-0 text-xs text-n-slate-11">
      {{
        t('TEAMS_SETTINGS.AGENTS.SELECTED_COUNT', {
          selected: selectedAgents.length,
          total: agentList.length,
        })
      }}
    </p>
    <NextButton
      type="submit"
      :label="submitButtonText"
      :disabled="disableSubmitButton"
      :is-loading="isWorking"
    />
  </div>
</template>
